import api from 'api/client';
import { ENDPOINTS } from 'api/endpoints';
import { STORAGE_KEYS } from 'constants/storageKeys';
import { tenantStorage } from 'utils/tenantStorage';
import { isAccessJwt } from 'utils/jwtClaims';
import { StoreAdminLoginRequest, StoreAdminSession } from 'types/admin';

type AdminAuthResponse = {
  accessToken?: string;
  AccessToken?: string;
  token?: string;
  Token?: string;
  expiresAt?: string;
  ExpiresAt?: string;
  displayName?: string;
  DisplayName?: string;
  csrfToken?: string;
  CsrfToken?: string;
  tenantCode?: string;
  TenantCode?: string;
};

const readString = (...values: Array<string | undefined>): string => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
};

const mapSession = (data: AdminAuthResponse): StoreAdminSession => {
  const rawToken = readString(data.accessToken, data.AccessToken, data.token, data.Token);
  return {
    accessToken: isAccessJwt(rawToken) ? rawToken : '',
    expiresAt: readString(data.expiresAt, data.ExpiresAt),
    displayName: readString(data.displayName, data.DisplayName) || undefined,
    csrfToken: readString(data.csrfToken, data.CsrfToken) || undefined,
    tenantCode: readString(data.tenantCode, data.TenantCode) || undefined,
  };
};

const clearLocalSession = () => {
  tenantStorage.removeItem(STORAGE_KEYS.ADMIN_TOKEN);
  tenantStorage.removeItem(STORAGE_KEYS.ADMIN_DISPLAY_NAME);
  tenantStorage.removeItem(STORAGE_KEYS.ADMIN_COOKIE_SESSION);
  tenantStorage.removeItem(STORAGE_KEYS.ADMIN_CSRF);
};

const storeAdminAuthService = {
  getToken: (): string | null => {
    const raw = tenantStorage.getItem(STORAGE_KEYS.ADMIN_TOKEN);
    if (isAccessJwt(raw)) return raw;
    if (raw) tenantStorage.removeItem(STORAGE_KEYS.ADMIN_TOKEN);
    return null;
  },

  hasCookieSession: (): boolean =>
    tenantStorage.getItem(STORAGE_KEYS.ADMIN_COOKIE_SESSION) === '1',

  getCsrfToken: (): string | null => tenantStorage.getItem(STORAGE_KEYS.ADMIN_CSRF),

  hasSession: (): boolean =>
    storeAdminAuthService.getToken() != null || storeAdminAuthService.hasCookieSession(),

  persistSession: (session: StoreAdminSession) => {
    if (isAccessJwt(session.accessToken)) {
      tenantStorage.setItem(STORAGE_KEYS.ADMIN_TOKEN, session.accessToken);
      tenantStorage.removeItem(STORAGE_KEYS.ADMIN_COOKIE_SESSION);
    } else {
      tenantStorage.removeItem(STORAGE_KEYS.ADMIN_TOKEN);
      tenantStorage.setItem(STORAGE_KEYS.ADMIN_COOKIE_SESSION, '1');
    }
    if (session.csrfToken) {
      tenantStorage.setItem(STORAGE_KEYS.ADMIN_CSRF, session.csrfToken);
      tenantStorage.setItem(STORAGE_KEYS.CUSTOMER_CSRF, session.csrfToken);
    }
    if (session.displayName) {
      tenantStorage.setItem(STORAGE_KEYS.ADMIN_DISPLAY_NAME, session.displayName);
    }
  },

  clearSession: () => {
    clearLocalSession();
  },

  login: async (payload: StoreAdminLoginRequest): Promise<StoreAdminSession> => {
    const { data } = await api.post<AdminAuthResponse>(ENDPOINTS.ADMIN_AUTH_LOGIN, payload);
    return mapSession(data);
  },

  logout: async (): Promise<void> => {
    sessionStorage.setItem('authRedirectReason', 'logout');
    try {
      await api.post(ENDPOINTS.ADMIN_AUTH_LOGOUT);
    } catch {
      // La cookie puede haber vencido; igual se limpia el estado local.
    } finally {
      clearLocalSession();
    }
  },

  exchangeToken: async (erpAccessToken: string): Promise<StoreAdminSession> => {
    const { data } = await api.post<AdminAuthResponse>(
      ENDPOINTS.ADMIN_AUTH_EXCHANGE,
      { erpAccessToken },
      { headers: { Authorization: `Bearer ${erpAccessToken}` } },
    );
    const session = mapSession(data);
    storeAdminAuthService.persistSession(session);
    return session;
  },

  acceptHandoffToken: (token: string, displayName?: string) => {
    storeAdminAuthService.persistSession({
      accessToken: token,
      expiresAt: new Date(Date.now() + 60 * 60 * 1000).toISOString(),
      displayName,
    });
  },
};

export default storeAdminAuthService;
